import { Dialog, makeStyles, DialogTitle, DialogContent, Box, DialogActions, Button, Divider, Typography, Card, CardContent, List, ListItem, ListItemText, ListItemIcon, CardHeader, useMediaQuery, CircularProgress } from "@material-ui/core";
import clsx from 'clsx';
import { MapBox } from 'components/AddressLocationForm/MapBox';
import useCustomModal from 'components/CustomConfirm';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import locationsService from 'services/locations.service';
import { isPayable, requiresLocationConfirmOnly, requiresLocationConfirmation, requiresNewLocation } from 'util/bookingStates';
import { formatAddressObject } from 'util/format';
import { parseAddressTypeToIcons } from 'util/parseAddressIcons';
import { useTrackEvent } from 'util/trackEvent';
import { isWaitingForPollState, manualRefreshState } from '../RecoilState';
import { confirmBooking } from 'services/booking.service';
import { isJourneyVisibleState } from '..';
import { AltLocationTypeSelectDialog } from "./Alt";

const useStyles = makeStyles(theme => ({
  dialog: {
    "z-index": `${theme.zIndex.modal - 1} !important `,
  },
  dialogPaper: {
    [theme.breakpoints.down("sm")]: {
      width: '100%',
    },
    [theme.breakpoints.up("md")]: {
      width: theme.breakpoints.values.sm,
    }
  },
  dialogFullContent: {
    overflowY: 'unset'
  },
  dialogScrollContent: {

  },
  dialogRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    alignContent: "center",
  },
  offsetPaper: {
    top: theme.spacing(5),
  },
  offsetScrollBody: {
    verticalAlign: 'top'
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  locationList: {
    minHeight: 200,
    overflowY: 'scroll',
    maxHeight: 200,
  },
  wrapper: {
    whiteSpace: 'normal'
  },
  cardHeaderAction: {
    margin: "auto"
  },
  cardHeaderRoot: {
    padding: theme.spacing(1),
    flexDirection: 'row',
    [theme.breakpoints.down("sm")]: {
      textAlign: 'center',
      flexDirection: 'column',
    }
  },
  addressIcon: {
    [theme.breakpoints.down("sm")]: {
      display: 'none',

    }
  },
  secondaryLocation: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  confirmedLocation:{

  },
  unconfirmedLocation: {
    color: theme.palette.text.hint,
  }
}));
const LocationTypeSelectDialog = ({ booking }) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const trackEvent = useTrackEvent()
  const mapRef = useRef(null)
  const setManualRefreshState = useSetRecoilState(manualRefreshState);
  const [isWaitingForPoll, setIsWaitingForPoll] = useRecoilState(isWaitingForPollState);
  const confirm = useCustomModal()
  const dialogTitle = useMemo(
    () => {
      let dialogTitle = ''
      if (requiresNewLocation(booking)) {
        dialogTitle = 'Step 1: Set your location'
      } else {
        dialogTitle = 'Step 1: Confirm your location'
      }
      return dialogTitle
    }, []
  );

  const [marker, setMarker] = useState({
    latitude: 0,
    longitude: 0
  });

  const [isJourneyVisible, setIsJourneyVisible] = useRecoilState(isJourneyVisibleState);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });
  useEffect(() => {
    const location = booking?.user_location || booking?.regional_office
    if (location) {
      setMarker({ longitude: location?.longitude, latitude: location?.latitude })
    }
  }, [location]);

  const [existingLocations, setExistingLocations] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState();

  const [updatedAddress, setUpdatedAddress] = useState();
  const fetchExistingLocations = useCallback(
    async () => {
      try {
        const locResult = await locationsService.getUserLocations(booking?.client?.id, { confirmedOnly: true })
        setExistingLocations(locResult)
      } catch(e) {
        
      }
    },
    [booking?.client?.id],
  );

  useEffect(() => {
    setUpdatedAddress(booking?.user_location)
    if (booking?.user_location) {
      setSelectedLocation(booking?.user_location)
    }
    fetchExistingLocations()
  }, []);


  useEffect(() => {
    if (mapRef?.current && marker?.latitude && marker?.longitude) {
      mapRef?.current?.setCenter([marker?.longitude, marker?.latitude]);
    }
  }, [marker]);

  const confirmLocationById = useCallback(
    async () => {
      confirm(props => {
        return (
          <CustomConfirmDialog {...props}
            description={<>
              <MapBox
                shrink
                draggable={false}
                center={{ latitude: selectedLocation?.latitude, longitude: selectedLocation?.longitude }}
                markerLocation={{ latitude: selectedLocation?.latitude, longitude: selectedLocation?.longitude }}
                onMarkerDragEnd={(lnglat) => { }}
              // onRef={(ref) => {
              //   mapRef.current = ref
              // }}
              />
              <Typography>
                {formatAddressObject(selectedLocation)}
              </Typography>
            </>
            }
            title={'Step 2: Provider will arrive at this location'}
            onConfirm={async () => {
              setIsWaitingForPoll(true)
              trackEvent('Confirm location by id - OK', { dialog: 'confirmLocationById', })
              if (selectedLocation?.id !== booking?.user_location?.id) {
                await locationsService.setWhitelabelUserLocationById(booking?.id, selectedLocation?.id)
              }
              await locationsService.confirmWhitelabelUserLocation(booking?.id)
              if (!booking?.requires_schedule_confirmation && booking?.requires_location_confirmation && !isPayable(booking)) {
                //disable confirm in case client wants to add more tests
                // await confirmBooking(booking?.id)
              }
              setManualRefreshState(Math.random())
              
              props.onClose()
            }}
            onCancel={() => {
              trackEvent('Confirm location by id - Cancel', { dialog: 'confirmLocationById' })
              props.onClose()
            }}
          />
        )
      })
    },
    [selectedLocation],
  );

  const renderConfirmButton = useMemo(
    () => {
      if (requiresNewLocation(booking)) {
        if (!selectedLocation) {
          return <></>
        }
      } else if (requiresLocationConfirmation(booking)) {
        if (!updatedAddress) {
          return <></>
        }
      }
        return <Button 
          onClick={() => { confirmLocationById() }} 
          color="primary" variant="contained"
          disabled={isWaitingForPoll}
          startIcon={isWaitingForPoll && <CircularProgress size={20} />}
          >Confirm</Button>
    }, [booking, selectedLocation, updatedAddress, isWaitingForPoll]
  );

  return (
    <Dialog
      open={requiresLocationConfirmation(booking)}
      onClose={null}
      disableEnforceFocus
      maxWidth="md"
      fullWidth
      // fullScreen={isMobile}
      className={classes.dialog}
      classes={{
        paper: classes.dialogRoot,
        paperScrollPaper: isJourneyVisible && classes.offsetPaper,
        paperScrollBody: isJourneyVisible && classes.offsetScrollBody,
      }}
      scroll="paper"
    >
      <CardHeader title={dialogTitle} />
      <DialogContent className={clsx(classes.dialogFullContent, classes.dialogPaper)}>
        <Typography variant="body2" color="textPrimary" gutterBottom>
          {formatAddressObject(booking?.user_location)}
        </Typography>
        <MapBox
          shrink
          draggable={false}
          center={marker}
          markerLocation={marker}
          onMarkerDragEnd={(lnglat) => { }}
          onRef={(ref) => {
            mapRef.current = ref
          }}
        />

        <DialogActions className={classes.actions}>
          {requiresLocationConfirmOnly(booking) && (
            <Button onClick={() => {
              trackEvent(`Location Dialog - ${requiresNewLocation(booking) ? 'Set' : 'Edit'} Address}`)
              history.push({
                pathname: `/bookings/${booking?.id}/location`,
                search: location.search
              });
            }} color="primary" variant="outlined">{requiresNewLocation(booking) ? 'Set' : 'Edit'} Address</Button>
          )}
          {renderConfirmButton}
        </DialogActions>
        {(requiresNewLocation(booking) && existingLocations?.length > 0) ? (
          <Card>
            <CardHeader
              classes={{
                root: classes.cardHeaderRoot,
                action: classes.cardHeaderAction
              }}
              subheader="Tap to select from saved locations"
              action={
                <Button size="small"
                  disabled={isWaitingForPoll}
                  startIcon={isWaitingForPoll && <CircularProgress size={20} />}
                  onClick={() => {
                    trackEvent(`Location Dialog - Add new location`)
                    history.push({
                      pathname: `/bookings/${booking?.id}/location`,
                      search: location.search
                    });
                  }}
                  color="primary" variant="contained">Add New Location</Button>
              } />
            <CardContent>
              <List dense className={classes.locationList}>
                {existingLocations?.map((loc, index) => (
                  <ListItem 
                    // disabled={!loc.is_confirmed}
                    selected={selectedLocation?.id === loc?.id}
                    button
                    onClick={() => {
                      setSelectedLocation(loc)
                      setMarker({ longitude: loc?.longitude, latitude: loc?.latitude })
                    }}
                    alignItems='flex-start' disableGutters divider>
                    <ListItemIcon className={classes.addressIcon}>{parseAddressTypeToIcons(loc?.type_name)}</ListItemIcon>
                    <ListItemText
                      secondary={<div className={classes.secondaryLocation}>
                        {loc.name}
                        {loc.is_confirmed ? <></> :
                          <Typography variant="caption" color="error">unconfirmed</Typography>
                        }
                      </div>}
                      primary={<Typography variant="body2" 
                        className={clsx(classes.wrapper, loc?.is_confirmed ? classes.confirmedLocation: classes.unconfirmedLocation)}
                      >
                        {formatAddressObject(loc)}
                      </Typography>
                      }
                    />
                  </ListItem>
                )
                )}
              </List>
            </CardContent>
          </Card>
        ) : (
          
            <CardHeader
              classes={{
                root: classes.cardHeaderRoot,
                action: classes.cardHeaderAction
              }}
              subheader="Tap to add a new location"
              action={
                <Button size="small"
                  onClick={() => {
                    trackEvent(`Location Dialog - Add new location`)
                    history.push({
                      pathname: `/bookings/${booking?.id}/location`,
                      search: location.search
                    });
                  }}
                  color="primary" variant="contained">Add New Location</Button>
              } />
          
        )}
      </DialogContent>
    </Dialog>
  )
}


// const ABLocationTypeSelectDialog = React.memo(AltLocationTypeSelectDialog)
const ABLocationTypeSelectDialog = React.memo(LocationTypeSelectDialog)

export { ABLocationTypeSelectDialog as LocationTypeSelectDialog }